/**
 * PHARMAKON — pricing engine · vocabularies.
 *
 * Frozen value lists of the engine. Three of them mirror Prisma enums of the refonte migrations
 * (`CanalVente` DB-22 and `OrigineMontantsChifa` DB-25 / M-14, `StatutCnasFactureChifa` DB-26 / M-15): the server test
 * apps/server/src/__tests__/pricing.parity.test.js compares them with the generated Prisma client
 * as soon as the enum exists, so they cannot drift once it lands (PK-BUG-15 lesson).
 * No business threshold lives here: tolerances and incentive rates are INPUTS (pharmacy settings,
 * A-06), never literals of the engine.
 */

/** A-01 — which of UG / UG % the user typed last (the master). */
export const MAITRE_UG = Object.freeze({ UG: "UG", UG_PCT: "UG_PCT" });

/**
 * CP §3.1 / D-25 (M-19) — the QTE reçue mode a facture d'achat snapshots at creation (mirrors Prisma
 * `ModeQteRecue`; the parity test pins the keys). It decides what the operator types in « Qté » and
 * what « Qté reçue » derives from; the stored meaning of a line is the same in both (A-74).
 */
export const MODE_QTE_RECUE = Object.freeze({
  PAYEE_PLUS_UG: "PAYEE_PLUS_UG", // « Qté reçue = Qté payée + UG » — the typed Qté is the paid units
  PAYEE_INCLUT_UG: "PAYEE_INCLUT_UG", // « Qté reçue = Qté payée, UG incluses » — the typed Qté holds the UG
});

/**
 * WP-63 — why the quantities typed on a line cannot be stored (the entry band says it, the server
 * refuses it): the codes of `quantitesStockees` (achat-quantites.js).
 */
export const ERREURS_QUANTITES_ACHAT = Object.freeze({
  // « UG incluses » (A-74): the typed Qté must leave at least one paid unit once the UG are taken out.
  UG_SANS_UNITE_PAYEE: "UG_SANS_UNITE_PAYEE",
  // A-75: more units received than the mode expects — a surplus is declared by a « Reçu non facturé » avoir.
  QUANTITE_RECUE_SUPERIEURE: "QUANTITE_RECUE_SUPERIEURE",
  // A-75 / A-99: the shortfall is attributed to the paid units, so Qté reçue never goes below the UG.
  QUANTITE_RECUE_INFERIEURE_AUX_UG: "QUANTITE_RECUE_INFERIEURE_AUX_UG",
});

/** D-01 / D-03 — the remise globale is typed in DA or as a % of Total HT. */
export const MAITRE_REMISE = Object.freeze({ MONTANT: "MONTANT", POURCENT: "POURCENT" });

/** D-09 — state of the TTC écart (ux/02 §2.3). Only TTC drives the rule. */
export const ETAT_ECART_TTC = Object.freeze({
  NON_SAISI: "NON_SAISI", // « Total TTC fournisseur non saisi » — validation blocked (OP-ACH-15)
  CONFORME: "CONFORME", // |écart| ≤ τ
  AVOIR_FINANCIER_REQUIS: "AVOIR_FINANCIER_REQUIS", // écart > τ — validable, flagged
  TOTAL_FOURNISSEUR_INFERIEUR: "TOTAL_FOURNISSEUR_INFERIEUR", // écart < −τ — warning only
});

/** The six declared totals of « Remplir totaux » (ux/02 §2.3) → computed figure, by DB column. */
export const TOTAUX_DECLARES = Object.freeze([
  Object.freeze({ cle: "ht", declare: "montant_ht_declare", calcule: "montant_ht_lignes_centimes" }),
  Object.freeze({ cle: "ht_remise", declare: "montant_ht_remise_declare", calcule: "montant_ht_centimes" }),
  Object.freeze({ cle: "tva", declare: "montant_tva_declare", calcule: "montant_tva_centimes" }),
  Object.freeze({ cle: "ppa", declare: "montant_ppa_declare", calcule: "montant_ppa_total_centimes" }),
  Object.freeze({ cle: "shp", declare: "montant_shp_declare", calcule: "montant_shp_total_centimes" }),
  Object.freeze({ cle: "ttc", declare: "montant_facture_reelle", calcule: "montant_ttc_centimes" }),
]);

/**
 * Domain states the engine can still compute but a writer must refuse (the validators of the
 * purchase WPs turn them into 400s; the SPA preview shows them). Structural errors (unreadable
 * amount, non-integer quantity) throw instead.
 */
export const ANOMALIES_PRICING = Object.freeze({
  QUANTITE_RECUE_SUPERIEURE: "QUANTITE_RECUE_SUPERIEURE", // D-08: 0 ≤ quantite_recue ≤ quantite
  RISTOURNE_SUPERIEURE_AU_BRUT: "RISTOURNE_SUPERIEURE_AU_BRUT", // DB-04: ristourne ≤ quantite × PU
  MONTANT_NEGATIF: "MONTANT_NEGATIF", // PU, ristourne, PPA, SHP, TVA, remise < 0
  REMISE_SUPERIEURE_AU_HT: "REMISE_SUPERIEURE_AU_HT", // D-03: HT remisé < 0
});

/** D-05 / REVENUE_MARGIN §4.3 — margin channel of a sale line (mirrors Prisma `CanalVente`, DB-22). */
export const CANAL_VENTE = Object.freeze({ COMPTOIR: "COMPTOIR", CHIFA: "CHIFA" });

/**
 * DB-25 / M-14 — where the CNAS amounts of a facture CHIFA come from (mirrors Prisma
 * `OrigineMontantsChifa`): the bridge session (authoritative CNAS figures) or the server estimate.
 */
export const ORIGINE_MONTANTS_CHIFA = Object.freeze({ CHIFA: "CHIFA", ESTIME: "ESTIME" });

/** D-05 — the two buckets of a lot. */
export const BUCKET_LOT = Object.freeze({ PAYEE: "PAYEE", UG: "UG" });

/**
 * D-06 / D-24 — CNAS status of a facture CHIFA (mirrors Prisma `StatutCnasFactureChifa`, DB-26).
 * Declaration order = lifecycle order.
 */
export const STATUT_CNAS = Object.freeze({
  EN_ATTENTE: "EN_ATTENTE", // sold, not in a clôturé bordereau
  DEPOSEE: "DEPOSEE", // in a CLOTURE / DEPOSE / VALIDE bordereau
  PAYEE: "PAYEE",
  PAYEE_AJUSTEE: "PAYEE_AJUSTEE", // paid below « dû »
  PAYEE_GLOBAL: "PAYEE_GLOBAL", // bordereau-level amount, allocated pro rata (OP-7)
  REJETEE: "REJETEE", // excluded / refused — stays non-realised (D-24)
});

/** Statuses that flip the CNAS share from théorique to réel (D-24). */
export const STATUTS_CNAS_REALISES = Object.freeze([
  STATUT_CNAS.PAYEE,
  STATUT_CNAS.PAYEE_AJUSTEE,
  STATUT_CNAS.PAYEE_GLOBAL,
]);

/**
 * Refonte WP-81 (CP §8.2, D-38, A-81; M-20) — how a « crédit fournisseur » (Écart avoir) relates to
 * the factures it names (mirrors Prisma `ModeRepartitionAvoir`; the parity test pins it):
 *   GLOBAL       « Montant global » — one amount; two or more factures are references only (no
 *                allocation is invented), one facture is imputed up to its open net dû, none = a
 *                fournisseur-level credit;
 *   PAR_FACTURE  « Répartition par facture » — an amount typed per facture, the total is their sum.
 */
export const MODE_REPARTITION_AVOIR = Object.freeze({ GLOBAL: "GLOBAL", PAR_FACTURE: "PAR_FACTURE" });

/**
 * Refonte WP-81 (CP §8.1-§8.2) — why a facture does or does not contribute to the suggested amount of
 * a crédit fournisseur (`suggestionEcart`). A positive écart is only ever a suggestion: no state here
 * makes a facture ineligible.
 */
export const ETAT_SUGGESTION_ECART = Object.freeze({
  POSITIF: "POSITIF", // flagged écart (above the tolerance, D-09) still to credit → contributes
  DEJA_CREDITE: "DEJA_CREDITE", // flagged, but the live Écart avoirs already credit all of it
  DANS_TOLERANCE: "DANS_TOLERANCE", // declared above computed, within the pharmacy tolerance (A-06)
  NEGATIF: "NEGATIF", // declared below computed — « à vérifier », never a credit (D-09)
  AUCUN: "AUCUN", // no declared TTC, or declared = computed
});

/**
 * Trésorerie TWP-57 (TO-11, TA-92 (3)) — the bound that caps what an open facture can take of an avoir's
 * fournisseur-level credit (`plafondImputationCredit`): its open amount (reste dû minus the INSTANCE
 * imputations committed on it), or — on a facture where the avoir carries a typed part (« Répartition
 * par facture ») — the rest of that part.
 */
export const LIMITE_IMPUTATION = Object.freeze({ RESTE: "RESTE", PART: "PART" });
